import {
  Package,
  AlertTriangle,
  Clock,
  DollarSign,
  ShoppingCart
} from 'lucide-react';
import { useDashboard, useChannelAnalytics } from '../hooks/useData';
import { formatCurrency, getStatusColor } from '../utils/api';

const COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981'];

function StatCard({ title, value, subtitle, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

function CriticalItemsTable({ items }) {
  if (!items || items.length === 0) {
    return <p className="text-gray-500 text-center py-4">No critical items</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">SKU</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Stock</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Days Left</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.sku} className="border-b border-gray-50 hover:bg-gray-50">
              <td className="py-3 px-4">
                <p className="font-medium text-gray-900 text-sm">{item.sku}</p>
                <p className="text-xs text-gray-500">{item.brand}</p>
              </td>
              <td className="py-3 px-4 text-sm text-gray-700">{item.current_stock}</td>
              <td className="py-3 px-4 text-sm text-gray-700">{item.days_of_stock}</td>
              <td className="py-3 px-4">
                <span className={getStatusColor(item.status)}>{item.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChannelDistribution({ data }) {
  if (!data || data.length === 0) return null;

  return (
    <div className="space-y-2">
      {data.map((channel, index) => (
        <div key={channel.channel} className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-gray-600">{channel.channel}</span>
          </div>
          <span className="font-medium text-gray-900">{channel.revenue_share}%</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { data: dashboard, isLoading, error } = useDashboard();
  const { data: channels } = useChannelAnalytics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        Error loading dashboard: {error.message}
      </div>
    );
  }

  const { summary, critical_items, overstock_items, upcoming_promos } = dashboard;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500">Inventory health overview</p>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total SKUs"
          value={summary.total_skus}
          subtitle={`${summary.healthy_count} healthy`}
          icon={Package}
          color="primary"
        />
        <StatCard
          title="Critical Items"
          value={summary.critical_count + summary.order_now_count}
          subtitle="Need immediate attention"
          icon={AlertTriangle}
          color="red"
        />
        <StatCard
          title="Avg Days of Stock"
          value={summary.avg_days_of_stock}
          subtitle="Across all SKUs"
          icon={Clock}
          color="yellow"
        />
        <StatCard
          title="Inventory Value"
          value={formatCurrency(summary.total_inventory_value_cost)}
          subtitle={`Retail: ${formatCurrency(summary.total_inventory_value_retail)}`}
          icon={DollarSign}
          color="green"
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Critical Items */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Critical Stock Alerts</h2>
            <span className={getStatusColor('CRITICAL')}>{critical_items?.length || 0} items</span>
          </div>
          <CriticalItemsTable items={critical_items} />
        </div>

        {/* Channel Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sales by Channel</h2>
          <ChannelDistribution data={channels} />
        </div>
      </div>

      {/* Third Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overstock Items */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Overstock Alert</h2>
            <span className={getStatusColor('OVERSTOCK')}>{overstock_items?.length || 0} items</span>
          </div>
          {overstock_items && overstock_items.length > 0 ? (
            <div className="space-y-3">
              {overstock_items.map((item) => (
                <div key={item.sku} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{item.sku}</p>
                    <p className="text-xs text-gray-500">{item.brand}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-purple-700">{item.days_of_stock} days</p>
                    <p className="text-xs text-gray-500">{item.current_stock} units</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No overstock items</p>
          )}
        </div>

        {/* Upcoming Promos */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Promotions</h2>
          </div>
          {upcoming_promos && upcoming_promos.length > 0 ? (
            <div className="space-y-3">
              {upcoming_promos.map((promo, index) => (
                <div key={index} className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">{promo.event_name}</p>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      +{Math.round(promo.expected_uplift * 100)}% uplift
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {promo.start_date} to {promo.end_date}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Channels: {promo.channels}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No upcoming promotions</p>
          )}
        </div>
      </div>

      {/* Pending Orders */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-yellow-50 rounded-lg">
            <ShoppingCart className="text-yellow-600" size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500">Pending Purchase Orders</p>
            <p className="text-xl font-bold text-gray-900">
              {summary.pending_po_count} orders | {formatCurrency(summary.pending_po_value)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
